/* Keep Shopify's collection filters and ordering; collect reviewed cards across
   every native page before applying the review-only pagination. */
(() => {
  function nativeURL(href) {
    const url = new URL(href);
    url.searchParams.delete('cc_reviews');
    url.searchParams.delete('cc_review_page');
    return url;
  }

  async function collectReviewed(firstURL, loadPage, signal) {
    const seenPages = new Set();
    const seenProducts = new Set();
    const cards = [];
    let url = nativeURL(firstURL);
    url.searchParams.delete('page');
    while (url) {
      if (signal?.aborted) throw new Error('Cancelled');
      if (seenPages.has(url.href)) throw new Error('Repeated collection page');
      seenPages.add(url.href);
      const page = await loadPage(url, signal);
      for (const card of page.cards) {
        if (card.count > 0 && !seenProducts.has(card.key)) {
          seenProducts.add(card.key);
          cards.push(card);
        }
      }
      const next = page.next ? nativeURL(new URL(page.next, url).href) : null;
      if (next && (next.origin !== url.origin || next.pathname !== url.pathname)) {
        throw new Error('Unexpected collection page');
      }
      url = next;
    }
    return cards;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { collectReviewed, nativeURL };
    return;
  }

  function initialize() {
  const root = document.querySelector('.cc-collection-page');
  if (!root || root.dataset.reviewsReady) return;
  root.dataset.reviewsReady = 'true';
  const group = root.querySelector('[data-review-filter]');
  const checkbox = group.querySelector('input');
  const current = new URL(window.location.href);
  group.hidden = false;
  checkbox.checked = current.searchParams.get('cc_reviews') === '1';
  if (!checkbox.checked) return;
  group.open = true;

  const grid = root.querySelector('.cc-shop-grid--collection');
  const nativePagination = root.querySelector('.cc-pagination');
  const status = document.createElement('p');
  status.className = 'cc-review-filter-status';
  status.setAttribute('role', 'status');
  grid.before(status);
  grid.hidden = true;
  if (nativePagination) nativePagination.hidden = true;
  root.querySelectorAll('.cc-active-filters a').forEach(link => {
    const url = new URL(link.href);
    url.searchParams.set('cc_reviews', '1');
    url.searchParams.delete('page');
    link.href = url.href;
  });
  const remove = document.createElement('a');
  const removeURL = nativeURL(current.href);
  removeURL.searchParams.delete('page');
  remove.href = removeURL.href;
  remove.textContent = 'With reviews only ×';
  root.querySelector('.cc-active-filters').append(remove);

  async function loadPage(url, signal) {
    const response = await fetch(url.href, { signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Collection unavailable');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const collection = doc.querySelector('.cc-collection-page');
    if (!collection) throw new Error('Collection missing');
    const cards = Array.from(collection.querySelectorAll('.cc-shop-grid--collection .cc-shop-card')).map(node => ({
      key: node.querySelector('a').getAttribute('href'),
      count: Number(node.dataset.reviewCount),
      node
    }));
    return { cards, next: collection.querySelector('.cc-pagination a[aria-label="Next page"]')?.getAttribute('href') };
  }

  async function run() {
    status.textContent = 'Finding products with reviews…';
    grid.setAttribute('aria-busy', 'true');
    try {
      const cards = await collectReviewed(current.href, loadPage);
      if (!root.isConnected) return;
      const totalPages = Math.max(1, Math.ceil(cards.length / 24));
      const page = Math.min(totalPages, Math.max(1, Number.parseInt(current.searchParams.get('cc_review_page'), 10) || 1));
      grid.replaceChildren(...cards.slice((page - 1) * 24, page * 24).map(card => document.importNode(card.node, true)));
      status.textContent = cards.length ? `${cards.length} ${cards.length === 1 ? 'product' : 'products'} with reviews` : 'No products with reviews match these filters.';
      grid.hidden = false;
      const pagination = document.createElement('nav');
      pagination.className = 'cc-pagination';
      pagination.setAttribute('aria-label', 'Pagination');
      if (totalPages > 1) {
        for (let i = 1; i <= totalPages; i++) {
          const item = document.createElement(i === page ? 'span' : 'a');
          item.textContent = i;
          if (i === page) item.setAttribute('aria-current', 'page');
          else {
            const url = new URL(current);
            url.searchParams.delete('page');
            url.searchParams.set('cc_review_page', i);
            item.href = url.href;
          }
          pagination.append(item);
        }
        grid.after(pagination);
      }
    } catch {
      if (!root.isConnected) return;
      status.textContent = 'Could not load products with reviews. ';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = 'Try again';
      retry.addEventListener('click', run, { once: true });
      status.append(retry);
    } finally {
      grid.removeAttribute('aria-busy');
    }
  }
  run();
  }
  initialize();
  document.addEventListener('shopify:section:load', initialize);
  new MutationObserver(records => {
    if (records.some(record => Array.from(record.addedNodes).some(node =>
      node.nodeType === 1 && (node.matches?.('.cc-collection-page') || node.querySelector?.('.cc-collection-page'))
    ))) initialize();
  }).observe(document.body, { childList: true, subtree: true });
})();
